import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModal from "../TransferTicketModal";
import TicketNotesModal from "../TicketNotesModal";
import toastError from "../../errors/toastError";
import { Can } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
	dispatchTicketDeleted,
	dispatchTicketUpdated
} from "../../utils/ticketEvents";

const getAssignmentMode = mode =>
	mode === "normal" ? "normal" : "multiagent";

const TicketOptionsMenu = ({ ticket, menuOpen, handleClose, anchorEl }) => {
	const history = useHistory();
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [clearChatConfirmationOpen, setClearChatConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [ticketNotesModalOpen, setTicketNotesModalOpen] = useState(false);
	const [syncMessagesModalOpen, setSyncMessagesModalOpen] = useState(false);
	const [autoSyncMessages, setAutoSyncMessages] = useState(true);
	const [syncPasses, setSyncPasses] = useState(1);
	const [syncChatIds, setSyncChatIds] = useState("");
	const [loadingSyncSettings, setLoadingSyncSettings] = useState(false);
	const [syncingMessages, setSyncingMessages] = useState(false);
	const [showCopyConversationOptions, setShowCopyConversationOptions] = useState(false);
	const [copyingConversationLimit, setCopyingConversationLimit] = useState(null);
	const isMounted = useRef(true);
	const { user, impersonation } = useContext(AuthContext);
	const assignmentModeStorageKey = `ticketAssignmentMode:${
		user?.companyId || user?.company?.id || "default"
	}`;
	const [assignmentMode, setAssignmentMode] = useState(() => {
		const storedMode = localStorage.getItem(assignmentModeStorageKey);
		return getAssignmentMode(storedMode);
	});
	const isNormalMode = assignmentMode === "normal";
	const canSeeAdvancedSyncOptions =
		user?.profile === "superadmin" ||
		impersonation?.originalUser?.profile === "superadmin";

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const storedMode = localStorage.getItem(assignmentModeStorageKey);
		if (storedMode) {
			setAssignmentMode(getAssignmentMode(storedMode));
		}

		const handleAssignmentModeChanged = event => {
			setAssignmentMode(getAssignmentMode(event?.detail?.mode));
		};

		window.addEventListener(
			"ticketAssignmentModeChanged",
			handleAssignmentModeChanged
		);

		return () => {
			window.removeEventListener(
				"ticketAssignmentModeChanged",
				handleAssignmentModeChanged
			);
		};
	}, [assignmentModeStorageKey]);

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
			dispatchTicketDeleted(ticket.id);
			history.push("/tickets");
		} catch (err) {
			toastError(err);
		}
	};

	const handleClearTicketMessages = async () => {
		try {
			const { data } = await api.delete(`/messages/${ticket.id}/clear`);
			dispatchTicketUpdated(data.ticket);
			toast.success(i18n.t("ticketOptionsMenu.clearChatSuccess"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenConfirmationModal = e => {
		setConfirmationOpen(true);
		handleClose();
	};

	const handleCloseMenu = () => {
		setShowCopyConversationOptions(false);
		handleClose();
	};

	const handleOpenClearChatConfirmationModal = e => {
		setClearChatConfirmationOpen(true);
		handleClose();
	};

	const handleOpenTransferModal = e => {
		setTransferTicketModalOpen(true);
		handleClose();
	};

	const handleOpenTicketNotesModal = e => {
		setTicketNotesModalOpen(true);
		handleClose();
	};

	const handleToggleIgnored = async () => {
		handleClose();
		try {
			const { data } = await api.put(`/tickets/${ticket.id}`, {
				isIgnored: !ticket.isIgnored,
			});
			dispatchTicketUpdated(data);
			toast.success(
				ticket.isIgnored ? "Chat sacado de ignorados" : "Chat movido a ignorados"
			);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenSyncMessagesModal = async () => {
		setSyncMessagesModalOpen(true);
		handleClose();
		setLoadingSyncSettings(true);

		try {
			const { data } = await api.get("/messages/sync/settings");
			setAutoSyncMessages(data.autoSync !== false);
		} catch (err) {
			toastError(err);
		}

		if (isMounted.current) {
			setLoadingSyncSettings(false);
		}
	};

	const copyTextToClipboard = async text => {
		if (navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(text);
			return;
		}

		const textarea = document.createElement("textarea");
		textarea.value = text;
		textarea.setAttribute("readonly", "");
		textarea.style.position = "fixed";
		textarea.style.opacity = "0";
		document.body.appendChild(textarea);
		textarea.select();
		document.execCommand("copy");
		document.body.removeChild(textarea);
	};

	const getMessageMetadata = message => {
		if (!message?.metadata) return {};
		if (typeof message.metadata === "object") return message.metadata;

		try {
			return JSON.parse(message.metadata);
		} catch (err) {
			return {};
		}
	};

	const getMediaTypeLabel = mediaType => {
		const labels = {
			audio: "audio",
			image: "imagen",
			video: "video",
			document: "documento",
			sticker: "sticker",
			vcard: "contacto",
			location: "ubicacion",
			poll: "encuesta",
			list: "lista"
		};

		return labels[mediaType] || mediaType || "archivo";
	};

	const parseLocationBody = (body = "") => {
		const parts = String(body || "").split("|");
		const firstPart = parts[0] || "";
		const secondPart = parts[1] || "";

		if (parts.length === 2) {
			return {
				link: firstPart,
				description: secondPart
			};
		}

		return {
			link: secondPart,
			description: parts.slice(2).join("|") || null
		};
	};

	const getListMetadata = message => {
		const list = getMessageMetadata(message).list || {};
		const sections = Array.isArray(list.sections) ? list.sections : [];

		return {
			title: list.title || message?.body || "Lista",
			body: list.body || "",
			footer: list.footer || "",
			sections: sections
				.map(section => ({
					title: section.title || "Opciones",
					rows: Array.isArray(section.rows)
						? section.rows
								.map(row => ({
									title: row.title || "",
									description: row.description || ""
								}))
								.filter(row => row.title)
						: []
				}))
				.filter(section => section.rows.length)
		};
	};

	const getConversationContextBodyLines = message => {
		const body = String(message?.body || "").trim();
		const metadata = getMessageMetadata(message);
		const mediaLabel = getMediaTypeLabel(message?.mediaType);
		const hasMediaAttachment = Boolean(
			message?.mediaUrl || (message?.mediaType && message.mediaType !== "chat")
		);
		const audioTranscriptionText = String(metadata.audioTranscription?.text || "").trim();
		const mediaTranscriptionText = String(metadata.mediaTranscription?.text || "").trim();
		const transcriptionLine = mediaTranscriptionText
			? `[${mediaLabel} transcrito] ${mediaTranscriptionText}`
			: audioTranscriptionText
			? `[audio transcrito] ${audioTranscriptionText}`
			: "";

		if (message?.mediaType === "poll") {
			const poll = metadata.poll || {};
			const options = Array.isArray(poll.options)
				? poll.options.map(option => option.name).filter(Boolean)
				: [];
			return [
				`[encuesta] ${poll.title || body || "Encuesta"}`,
				...options.map(option => `- ${option}`)
			];
		}

		if (message?.mediaType === "list") {
			const list = getListMetadata(message);
			return [
				`[lista] ${list.title}`,
				list.body,
				...list.sections.flatMap(section => [
					section.title,
					...section.rows.map(row =>
						row.description ? `- ${row.title}: ${row.description}` : `- ${row.title}`
					)
				]),
				list.footer
			].filter(Boolean);
		}

		if (message?.mediaType === "location") {
			const location = parseLocationBody(message.body || "");
			return [
				`[ubicacion] ${location.description || location.link || body || "Ubicacion"}`
			];
		}

		if (message?.mediaType === "vcard") {
			return [`[contacto] ${body || "Contacto compartido"}`];
		}

		if (body) {
			const formattedBody = hasMediaAttachment ? `[${mediaLabel}] ${body}` : body;

			return [
				formattedBody,
				...(transcriptionLine ? [transcriptionLine] : [])
			];
		}

		if (transcriptionLine) return [transcriptionLine];
		if (hasMediaAttachment) return [`[${mediaLabel}]`];
		return ["[mensaje vacio]"];
	};

	const getConversationContextAuthor = message =>
		message.fromMe
			? "Agente"
			: message.contact?.name || ticket.contact?.name || "Cliente";

	const formatConversationContextMessage = message => {
		const author = getConversationContextAuthor(message);
		const lines = getConversationContextBodyLines(message);

		return lines
			.map((line, index) => (index === 0 ? `${author}: ${line}` : line))
			.join("\n");
	};

	const getMessageDateValue = message =>
		new Date(message.createdAt || 0).getTime() || 0;

	const loadRecentConversationMessages = async limit => {
		const messagesById = new Map();
		let page = 1;
		let hasMorePages = true;
		const maxPages = Math.ceil(limit / 20) + 2;
		const validMessagesCount = () =>
			Array.from(messagesById.values()).filter(
				message => !message.isDeleted && !message.internalNote
			).length;

		while (validMessagesCount() < limit && hasMorePages && page <= maxPages) {
			const { data } = await api.get(`/messages/${ticket.id}`, {
				params: { pageNumber: page, readOnly: "true" }
			});

			(data.messages || []).forEach(message => {
				if (message?.id) messagesById.set(String(message.id), message);
			});

			hasMorePages = Boolean(data.hasMore);
			page += 1;
		}

		return Array.from(messagesById.values())
			.filter(message => !message.isDeleted && !message.internalNote)
			.sort((a, b) => {
				const timeDiff = getMessageDateValue(a) - getMessageDateValue(b);
				if (timeDiff !== 0) return timeDiff;
				return String(a.id).localeCompare(String(b.id));
			})
			.slice(-limit);
	};

	const handleCopyConversationContext = async limit => {
		const cleanLimit = Number(limit);
		const safeLimit = [10, 20, 30, 50].includes(cleanLimit) ? cleanLimit : 20;

		setShowCopyConversationOptions(false);
		setCopyingConversationLimit(safeLimit);
		handleClose();

		try {
			const contextMessages = await loadRecentConversationMessages(safeLimit);

			if (!contextMessages.length) {
				toast.info("No hay mensajes para copiar.");
				return;
			}

			const context = contextMessages.map(formatConversationContextMessage).join("\n\n");

			await copyTextToClipboard(context);
			toast.success(`${contextMessages.length} mensajes copiados.`);
		} catch (err) {
			toastError(err);
		}

		if (isMounted.current) {
			setCopyingConversationLimit(null);
		}
	};

	const handleSyncMessages = async ({ closeModal = false } = {}) => {
		setSyncingMessages(true);
		handleClose();

		try {
			const passes = Math.max(1, Number(syncPasses) || 1);
			const chatIds = syncChatIds
				.split(",")
				.map(value => value.trim())
				.filter(Boolean);
			const { data } = await api.post(`/messages/${ticket.id}/sync`, {
				passes,
				chatIds
			});
			const count = Number(data.synced || 0) + Number(data.updated || 0);

			toast.success(
				i18n.t("ticketOptionsMenu.syncSuccess", { count })
			);
		} catch (err) {
			toastError(err);
		}

		if (isMounted.current) {
			setSyncingMessages(false);
			if (closeModal) {
				setSyncMessagesModalOpen(false);
			}
		}
	};

	const handleSaveSyncSettings = async () => {
		setSyncingMessages(true);

		try {
			await api.put("/messages/sync/settings", {
				autoSync: autoSyncMessages
			});
			toast.success(i18n.t("ticketOptionsMenu.syncSettingsSaved"));
			setSyncMessagesModalOpen(false);
		} catch (err) {
			toastError(err);
		}

		if (isMounted.current) {
			setSyncingMessages(false);
		}
	};

	const handleManualSyncFromModal = async () => {
		try {
			await api.put("/messages/sync/settings", {
				autoSync: false
			});
			setAutoSyncMessages(false);
		} catch (err) {
			toastError(err);
			return;
		}

		await handleSyncMessages({ closeModal: true });
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};

	const handleCloseTicketNotesModal = () => {
		if (isMounted.current) {
			setTicketNotesModalOpen(false);
		}
	};

	return (
		<>
			<Menu
				id="menu-appbar"
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				keepMounted
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuOpen}
				onClose={handleCloseMenu}
			>
				{!isNormalMode && (
					<MenuItem onClick={handleOpenTransferModal}>
						{i18n.t("ticketOptionsMenu.transfer")}
					</MenuItem>
				)}
				<MenuItem onClick={handleOpenTicketNotesModal}>
					{i18n.t("ticketOptionsMenu.notes")}
				</MenuItem>
				<MenuItem onClick={handleOpenSyncMessagesModal} disabled={syncingMessages}>
					{i18n.t("ticketOptionsMenu.syncMessages")}
				</MenuItem>
				<MenuItem
					disabled={Boolean(copyingConversationLimit)}
					onClick={() =>
						setShowCopyConversationOptions(current => !current)
					}
				>
					{copyingConversationLimit
						? "Copiando conversacion..."
						: "Copiar conversacion"}
				</MenuItem>
				{showCopyConversationOptions &&
					[10, 20, 30, 50].map(limit => (
						<MenuItem
							key={limit}
							disabled={copyingConversationLimit === limit}
							onClick={() => handleCopyConversationContext(limit)}
						>
							{copyingConversationLimit === limit
								? `Copiando ${limit}...`
								: `Ultimos ${limit} mensajes`}
						</MenuItem>
					))}
				<MenuItem onClick={handleToggleIgnored}>
					{ticket.isIgnored ? "Sacar de Ignorados" : "Mover a Ignorados"}
				</MenuItem>
				<Can
					role={user.profile}
					perform="ticket-options:deleteTicket"
					yes={() => (
						<MenuItem onClick={handleOpenClearChatConfirmationModal}>
							{i18n.t("ticketOptionsMenu.clearChat")}
						</MenuItem>
					)}
				/>
				<Can
					role={user.profile}
					perform="ticket-options:deleteTicket"
					yes={() => (
						<MenuItem onClick={handleOpenConfirmationModal}>
							{i18n.t("ticketOptionsMenu.delete")}
						</MenuItem>
					)}
				/>
			</Menu>
			<ConfirmationModal
				title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${
					ticket.id
				} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${
					ticket.contact.name
				}?`}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				{i18n.t("ticketOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
			<ConfirmationModal
				title={`${i18n.t("ticketOptionsMenu.clearChatConfirmationModal.title", {
					defaultValue: "Vaciar chat #"
				})}${
					ticket.id
				} ${i18n.t("ticketOptionsMenu.clearChatConfirmationModal.titleFrom", {
					defaultValue: "del contacto "
				})} ${
					ticket.contact.name
				}?`}
				open={clearChatConfirmationOpen}
				onClose={setClearChatConfirmationOpen}
				onConfirm={handleClearTicketMessages}
			>
				{i18n.t("ticketOptionsMenu.clearChatConfirmationModal.message", {
					defaultValue:
						"Atencion! Se borraran todos los mensajes de este chat, pero el chat se conservara."
				})}
			</ConfirmationModal>
			<TransferTicketModal
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
				ticketConnectionId={ticket.connectionId}
			/>
			<TicketNotesModal
				modalOpen={ticketNotesModalOpen}
				onClose={handleCloseTicketNotesModal}
				ticket={ticket}
			/>
			<Dialog
				open={syncMessagesModalOpen}
				onClose={() => setSyncMessagesModalOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>{i18n.t("ticketOptionsMenu.syncModal.title")}</DialogTitle>
				<DialogContent dividers>
					<FormControlLabel
						control={
							<Checkbox
								color="primary"
								checked={autoSyncMessages}
								disabled={loadingSyncSettings || syncingMessages}
								onChange={event => setAutoSyncMessages(event.target.checked)}
							/>
						}
						label={i18n.t("ticketOptionsMenu.syncModal.autoSyncLabel")}
					/>
					<Typography variant="body2" color="textSecondary">
						{i18n.t("ticketOptionsMenu.syncModal.description")}
					</Typography>
					{canSeeAdvancedSyncOptions && (
						<>
							<TextField
								fullWidth
								margin="dense"
								label="Pasadas"
								type="number"
								value={syncPasses}
								disabled={loadingSyncSettings || syncingMessages}
								inputProps={{ min: 1, max: 10 }}
								onChange={event => setSyncPasses(event.target.value)}
							/>
							<TextField
								fullWidth
								margin="dense"
								label="Chat IDs"
								value={syncChatIds}
								disabled={loadingSyncSettings || syncingMessages}
								onChange={event => setSyncChatIds(event.target.value)}
							/>
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setSyncMessagesModalOpen(false)}
						disabled={syncingMessages}
					>
						{i18n.t("ticketOptionsMenu.buttons.cancel")}
					</Button>
					{!autoSyncMessages && (
						<Button
							color="primary"
							onClick={handleManualSyncFromModal}
							disabled={syncingMessages || loadingSyncSettings}
						>
							{i18n.t("ticketOptionsMenu.syncModal.syncNow")}
						</Button>
					)}
					<Button
						color="primary"
						variant="contained"
						onClick={handleSaveSyncSettings}
						disabled={syncingMessages || loadingSyncSettings}
					>
						{i18n.t("ticketOptionsMenu.syncModal.save")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default TicketOptionsMenu;

